import * as React from 'react'
import { ArrowUp, Maximize2 } from 'lucide-react'

import { IvyWordmark } from '@/components/brand/ivy-wordmark'
import { IconButton } from '@/components/ui/icon-button'
import { cn } from '@/lib/utils'

/** Max characters accepted by the composer. */
export const MAX_CHAT_INPUT_LENGTH = 2000

/** Animated ghost-placeholder prompts shown while the input is empty/unfocused. */
const DEFAULT_PROMPTS = [
  'Show me scalping setups right now with strong momentum and tight spreads.',
  'Find liquid names with unusual call volume and rising implied volatility today.',
  'Which of my holdings report earnings this week, and how are options priced?',
  'Scan for oversold large caps near support showing bullish RSI divergence.',
]

// Typewriter cadence (ms).
const TYPE_MS = 75
const ERASE_MS = 30
const HOLD_MS = 1000

export interface ChatInputProps {
  /** Current textarea value (controlled). */
  value: string
  /** Called with the next value on every edit. */
  onChange: (value: string) => void
  /** Called with the current value when the user submits (Enter / send button). */
  onSubmit: (value: string) => void
  disabled?: boolean
  /** Static placeholder used when the typewriter isn't running (focus / reduced-motion). */
  placeholder?: string
  /** Character cap; submit is blocked above it. Defaults to {@link MAX_CHAT_INPUT_LENGTH}. */
  maxLength?: number
  /** Slot for leading controls (attach / module buttons) at the start of the input row. */
  leadingControls?: React.ReactNode
  /** Header tagline beside the Ivy wordmark. */
  title?: string
  /** When provided, renders an expand button in the header. */
  onExpand?: () => void
  /** Prompts cycled by the typewriter ghost placeholder. Pass `[]` to disable it. */
  animatedPrompts?: string[]
  /** Accessible name for the textarea. */
  inputAriaLabel?: string
  /** Renders the subtle radial top-glow accent across the top edge of the card. */
  topGlow?: boolean
  /** When true, submit is allowed with an empty value (marketing hero CTA). */
  allowEmptySubmit?: boolean
  /** Extra classes on the send button (e.g. hover accent on the hero). */
  submitButtonClassName?: string
  className?: string
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

function usePrefersReducedMotion(): boolean {
  return React.useSyncExternalStore(
    (onStoreChange) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {}
      const mq = window.matchMedia(REDUCED_MOTION_QUERY)
      mq.addEventListener?.('change', onStoreChange)
      return () => mq.removeEventListener?.('change', onStoreChange)
    },
    () =>
      typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia(REDUCED_MOTION_QUERY).matches
        : false,
    () => false
  )
}

export function ChatInput({
  value,
  onChange,
  onSubmit,
  disabled = false,
  placeholder,
  maxLength = MAX_CHAT_INPUT_LENGTH,
  leadingControls,
  title = 'your AI trading companion',
  onExpand,
  animatedPrompts = DEFAULT_PROMPTS,
  inputAriaLabel = 'Chat message',
  topGlow = false,
  allowEmptySubmit = false,
  submitButtonClassName,
  className,
}: ChatInputProps) {
  const [focused, setFocused] = React.useState(false)
  const [typed, setTyped] = React.useState('')
  const reducedMotion = usePrefersReducedMotion()

  const prompts = animatedPrompts
  const showGhost =
    !disabled &&
    value.length === 0 &&
    !focused &&
    !reducedMotion &&
    prompts.length > 0

  // Typewriter: type a prompt, hold ~1s, erase, advance, loop. Runs only while
  // `showGhost` is true; never touches the controlled `value`.
  React.useEffect(() => {
    // The overlay only renders while `showGhost` is true, so when it's false we
    // simply stop — no state reset needed (and none allowed synchronously here).
    if (!showGhost) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>
    let promptIndex = 0
    let charIndex = 0

    const type = () => {
      if (cancelled) return
      const current = prompts[promptIndex % prompts.length]
      if (charIndex < current.length) {
        charIndex += 1
        setTyped(current.slice(0, charIndex))
        timer = setTimeout(type, TYPE_MS)
      } else {
        timer = setTimeout(erase, HOLD_MS)
      }
    }
    const erase = () => {
      if (cancelled) return
      const current = prompts[promptIndex % prompts.length]
      if (charIndex > 0) {
        charIndex -= 1
        setTyped(current.slice(0, charIndex))
        timer = setTimeout(erase, ERASE_MS)
      } else {
        promptIndex += 1
        timer = setTimeout(type, TYPE_MS)
      }
    }

    timer = setTimeout(type, TYPE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [showGhost, prompts])

  const canSubmit =
    !disabled &&
    value.length <= maxLength &&
    (allowEmptySubmit || value.trim().length > 0)

  const submit = () => {
    if (canSubmit) onSubmit(value)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submit()
    }
  }

  // While the ghost animates, the textarea's placeholder mirrors the typed text
  // (rendered transparent via `placeholder:text-transparent`) so `field-sizing:
  // content` grows the field to fit the wrapped prompt — exactly like real typing.
  // The visible muted text + caret are drawn by the overlay below. Otherwise the
  // field stays clean on focus; the first prompt is a static fallback for the
  // non-animated, unfocused, empty case (e.g. reduced motion).
  const showStaticPrompt =
    !showGhost && !focused && value.length === 0 && prompts.length > 0
  const nativePlaceholder = showGhost
    ? typed
    : (placeholder ?? (showStaticPrompt ? prompts[0] : ''))

  return (
    <div
      className={cn(
        'relative flex w-full flex-col gap-3 overflow-hidden rounded-[var(--corner-radius-xxl)] border border-border bg-card px-4 py-[1.6rem]',
        className
      )}
    >
      {topGlow ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0"
          style={{
            height: 'var(--ivy-chat-top-border-height)',
            background: 'var(--ivy-chat-top-border-gradient)',
          }}
        />
      ) : null}
      <div className="flex items-center gap-2">
        <IvyWordmark height={24} aria-hidden />
        {title ? (
          <span className="text-sm text-muted-foreground">{title}</span>
        ) : null}
        {onExpand ? (
          <IconButton
            variant="ghost"
            size="sm"
            aria-label="Expand"
            onClick={onExpand}
            disabled={disabled}
            className="ml-auto"
          >
            <Maximize2 />
          </IconButton>
        ) : null}
      </div>

      <div className="flex min-h-[4.4rem] items-center gap-2 rounded-[var(--corner-radius-xl)] border border-border bg-background p-2 focus-within:ring-2 focus-within:ring-ring">
        {leadingControls}

        <div className="relative flex-1">
          <textarea
            aria-label={inputAriaLabel}
            className={cn(
              // On phones, pre-size to ~5 lines (min-h) so the animated prompts
              // wrap inside the field instead of growing it and pushing content
              // down. Desktop stays compact (md:min-h-0).
              // pt-[0.8125rem] = p-2 (8px) + 5px, nudging the text 5px lower.
              'field-sizing-content min-h-[10.75rem] max-h-48 w-full resize-none border-0 bg-transparent p-2 pt-[0.8125rem] text-left text-[1.2rem] leading-relaxed text-foreground outline-none md:min-h-0',
              // Transparent while animating: the placeholder only drives sizing;
              // the overlay supplies the visible text.
              showGhost ? 'placeholder:text-transparent' : 'placeholder:text-muted-foreground'
            )}
            value={value}
            placeholder={nativePlaceholder}
            maxLength={maxLength}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              setFocused(true)
              setTyped('')
            }}
            onBlur={() => setFocused(false)}
            rows={1}
          />
          {showGhost ? (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 overflow-hidden p-2 pt-[0.8125rem] text-left text-[1.2rem] leading-relaxed text-muted-foreground"
            >
              <span className="whitespace-pre-wrap break-words">
                {typed}
                <span className="ml-0.5 inline-block h-5 w-px animate-pulse bg-muted-foreground align-middle" />
              </span>
            </div>
          ) : null}
        </div>

        <IconButton
          variant="filled"
          size="md"
          aria-label="Send message"
          className={cn('rounded-full', submitButtonClassName)}
          disabled={!canSubmit}
          onClick={submit}
        >
          <ArrowUp />
        </IconButton>
      </div>
    </div>
  )
}
